import React, { forwardRef, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  isPersistable,
  hasError,
  hasSessionScanPendingV2,
  getActiveEntities,
} from '@ublend-npm/aula-editor';
import { getFileScanStatus } from '../../../../../core/selectors/fileScan';
import { isAntivirusFileScanEnabled } from '../../../../../core/constants/launchDarklyFeatureFlags';
import * as actionTypes from '../../../../../core/constants/actions';

const isAnyFileUploading = (entities = {}) =>
  Object.values(entities).some(
    (entity: any) =>
      entity.type === 'file' &&
      !entity.failed &&
      (!entity.data?.fileUrl || entity.transient?.progress != null)
  );

/**
 * Injects realtime file scan status from Redux into editor config (Parse file id -> status).
 * Only when FEAT_ANTIVIRUS_CHECK_IN_FILE_UPLOAD is true in LaunchDarkly.
 */

const withFileScanFromRedux = (AulaEditor) => {
  const WithFileScan = forwardRef(
    (
      {
        fileScanStatus,
        fileScanBySession,
        resetEntityScanStatus,
        config,
        spaceId,
        ...rest
      }: any,
      ref
    ) => {
      const { editorState, onFilesStatusChange, readOnly, isSubmitting } = rest;
      const flags = useFlags();
      let enableFileScanStatus = isAntivirusFileScanEnabled(flags);

      const mergedConfig = useMemo(
        () => ({
          ...config,
          spaceId,
          ...(enableFileScanStatus &&
            (!readOnly || isSubmitting || onFilesStatusChange) && {
              fileScanStatus,
              fileScanBySession,
              enableFileScanStatus,
            }),
        }),
        [
          config,
          enableFileScanStatus,
          spaceId,
          fileScanStatus,
          fileScanBySession,
          readOnly,
          isSubmitting,
          onFilesStatusChange,
        ]
      );

      if (mergedConfig?.enableFileScanStatus) {
        mergedConfig.enableFileScanStatus = enableFileScanStatus;
      }
      const antivirusFileScanEnabled = enableFileScanStatus;
      const fullConfig = mergedConfig;

      const isFileUploading = useMemo(() => {
        return Object.keys(editorState?.context?.entities || {}).length > 0
          ? isAnyFileUploading(getActiveEntities(editorState))
          : false;
      }, [editorState]);

      const isFilePresistable = useMemo(
        () =>
          Object.keys(editorState?.context?.entities || {}).length > 0
            ? isPersistable(editorState, fullConfig)
            : true,
        [editorState, fullConfig]
      );

      const haveError = useMemo(
        () =>
          Object.keys(editorState?.context?.entities || {}).length > 0
            ? hasError(editorState, fullConfig)
            : false,
        [editorState, fullConfig]
      );

      const prevUpdating = useRef(false);
      const prevStatus = useRef({
        isUpdating: false,
        isPersistable: false,
        hasError: false,
      });

      useEffect(() => {
        if (onFilesStatusChange) {
          onFilesStatusChange({
            isUpdating: isFileUploading,
            isPersistable: isFilePresistable,
            hasError: haveError,
          });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      useEffect(() => {
        if (!antivirusFileScanEnabled && onFilesStatusChange) {
          onFilesStatusChange({
            isUpdating: isFileUploading,
            isPersistable: isFilePresistable,
            hasError: haveError,
          });
          return;
        }

        if (antivirusFileScanEnabled && onFilesStatusChange) {
          const isUpdating =
            isFileUploading || hasSessionScanPendingV2(fullConfig);

          const persistable = isFilePresistable;
          const error = haveError;

          prevUpdating.current = isUpdating;
          const statusChanged =
            prevStatus.current.isUpdating !== isUpdating ||
            prevStatus.current.isPersistable !== persistable ||
            prevStatus.current.hasError !== error;

          if (statusChanged) {
            prevStatus.current = {
              isUpdating,
              isPersistable: persistable,
              hasError: error,
            };
            onFilesStatusChange(prevStatus.current);
          }
        }
      }, [
        fullConfig?.fileScanBySession,
        editorState,
        onFilesStatusChange,
        antivirusFileScanEnabled,
        isFileUploading,
        isFilePresistable,
        haveError,
        fullConfig,
      ]);

      return (
        <AulaEditor
          ref={ref}
          {...rest}
          resetEntityScanStatus={resetEntityScanStatus}
          config={mergedConfig}
        />
      );
    }
  );

  WithFileScan.displayName = 'WithFileScan';

  WithFileScan.propTypes = {
    config: PropTypes.shape({}),
    spaceId: PropTypes.string,
    fileScanStatus: PropTypes.objectOf(PropTypes.string),
    fileScanBySession: PropTypes.objectOf(
      PropTypes.shape({
        status: PropTypes.string,
        correlationId: PropTypes.string,
        s3Key: PropTypes.string,
      })
    ),
    resetEntityScanStatus: PropTypes.func.isRequired,
  };

  WithFileScan.defaultProps = {
    config: {},
    spaceId: undefined,
    fileScanStatus: {},
    fileScanBySession: {},
  };

  const mapStateToProps = (state) => ({
    fileScanStatus: getFileScanStatus(state),
    fileScanBySession: state.data.fileScanBySession,
    spaceId: state.data.classRooms[state.classRoom.current]?.objectId,
  });

  const mapDispatchToProps = (dispatch) => ({
    resetEntityScanStatus: (id, s3Key) =>
      dispatch({
        type: actionTypes.RESET_FILE_SCAN_STATUS,
        payload: { id, s3Key },
      }),
  });

  return connect(mapStateToProps, mapDispatchToProps, null, {
    forwardRef: true,
  })(WithFileScan);
};

export default withFileScanFromRedux;
